/**
 * Os ícones dos cartões de módulo, um componente por cartão: dez para a tela
 * inicial e dois para o submenu da Fazenda Boa Vista dentro de Residências.
 *
 * Papel: cada função devolve o `<svg>` de um cartão — traço de 1,5px, sem
 * preenchimento, pontas e junções arredondadas, no estilo Feather —, com o
 * `path`, `circle`, `rect`, `line` ou `polyline` reproduzido caractere a
 * caractere. O `IconeUsuarios` é a exceção, sem original contra o qual
 * conferir: mexer nele não quebra fidelidade nenhuma, desde que continue com os
 * `ATRIBUTOS_COMUNS` e com o busto na mesma proporção do `IconeRh`, ou ele
 * passa a destoar dos outros nove na grade de quem administra.
 *
 * Como usar: quem monta um cartão passa o componente inteiro para
 * `CartaoDeModulo`, nunca o elemento já montado — é ele quem decide o tamanho e
 * quem aplica a cor. Todo traço usa `stroke="currentColor"`, e é isso que faz o
 * ícone herdar `--brass` do texto do cartão e virar `--brass-light` junto com
 * ele no hover, sem precisar de uma classe de cor própria em cada `<svg>`. O
 * `aria-hidden` já vem marcado em cada um, porque o nome do cartão ao lado do
 * ícone, dentro do mesmo link, já é o texto que o leitor de tela anuncia.
 */
import { type SVGProps } from 'react';

/** Atributos comuns a todos os `<svg>`, para não repeti-los em cada componente. */
const ATRIBUTOS_COMUNS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * Ícone do cartão de Finanças: um gráfico ascendente.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeFinancas(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <polyline points="3,17 9,11 13,15 21,5" />
      <polyline points="15,5 21,5 21,11" />
    </svg>
  );
}

/**
 * Ícone do cartão de RH doméstico: um busto.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeRh(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c0-4.1 3.4-6.8 7.5-6.8s7.5 2.7 7.5 6.8" />
    </svg>
  );
}

/**
 * Ícone do cartão de Veículos: um carro.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeVeiculos(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M4 16V11.5L6.2 6.8A2 2 0 0 1 8 5.5h8a2 2 0 0 1 1.8 1.3L20 11.5V16" />
      <path d="M4 16h16v2.5a1 1 0 0 1-1 1h-1.2a1 1 0 0 1-1-1V17H7.2v1.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V16z" />
      <circle cx="7.5" cy="16" r="1.2" />
      <circle cx="16.5" cy="16" r="1.2" />
    </svg>
  );
}

/**
 * Ícone do cartão de Residências: uma casa.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeResidencias(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M4 11.5L12 4l8 7.5" />
      <path d="M6 10v9.5h12V10" />
      <path d="M10 19.5v-6h4v6" />
    </svg>
  );
}

/**
 * Ícone do cartão de Compras & estoque: uma sacola.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeCompras(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M7.5 8V6a4.5 4.5 0 0 1 9 0v2" />
      <rect x="4" y="8" width="16" height="12" rx="2" />
    </svg>
  );
}

/**
 * Ícone do cartão de Saúde: uma cruz médica.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeSaude(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <rect x="10" y="4" width="4" height="16" rx="1" />
      <rect x="4" y="10" width="16" height="4" rx="1" />
    </svg>
  );
}

/**
 * Ícone do cartão de Documentos & segurança: um escudo.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeDocumentos(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M12 3.5l7 2.6v5.4c0 4.6-3 7.7-7 8.9-4-1.2-7-4.3-7-8.9V6.1z" />
    </svg>
  );
}

/**
 * Ícone do cartão de Mailing: um envelope.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeMailing(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </svg>
  );
}

/**
 * Ícone do cartão de Orçamentos: um marcador.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeOrcamentos(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M7 3.5h10a1 1 0 0 1 1 1V20l-3-2-3 2-3-2-3 2V4.5a1 1 0 0 1 1-1z" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="9" y1="12" x2="15" y2="12" />
    </svg>
  );
}

/**
 * Ícone do cartão de Usuários e permissões: dois bustos, um à frente do outro.
 *
 * O segundo busto é o que o distingue do `IconeRh`, que tem um só: os dois
 * cartões aparecem na mesma grade de quem administra, e um busto igual nos
 * dois faria a pessoa ler o nome para saber qual é qual.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeUsuarios(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.5 19.5c0-3.6 2.7-6 6-6s6 2.4 6 6" />
      <path d="M15 5.6a3.2 3.2 0 0 1 0 5.8" />
      <path d="M17.5 13.9c1.9.7 3 2.8 3 5.6" />
    </svg>
  );
}

/**
 * Ícone do cartão de Manutenções, no submenu da Fazenda Boa Vista: uma chave
 * inglesa.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeManutencoes(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" />
    </svg>
  );
}

/**
 * Ícone do cartão de Calendário, no submenu da Fazenda Boa Vista: uma folhinha.
 *
 * @param props - Atributos de `<svg>` de quem chama, aplicados depois dos
 *   comuns e por isso capazes de sobrescrevê-los.
 * @returns O `<svg>` do ícone.
 */
export function IconeCalendario(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...ATRIBUTOS_COMUNS} {...props}>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </svg>
  );
}
